import { type UUID } from '~/types/common';

import { type InvoiceStopperRulesQueryParameters } from './useQueryInvoiceStopperRules';
import { type GetReprocessingInvoicesPayload } from './useQueryReprocessingInvoices';
import { type GetReprocessingInvoicesCountsPayload } from './useQueryReprocessingInvoicesCounts';

/**
 * Query key factory for invoice reprocessing-related queries.
 */
export const queryKeysInvoiceReprocessing = {
  base: () => 'invoiceReprocessing' as const,
  get: (processId: UUID) =>
    [queryKeysInvoiceReprocessing.base(), 'details', processId] as const,
  getAll: (searchParams: GetReprocessingInvoicesPayload) =>
    [queryKeysInvoiceReprocessing.base(), 'list', searchParams] as const,
  getFile: ({
    fileId,
    fileType,
    processId,
  }: {
    fileId: UUID;
    fileType: any;
    processId: UUID;
  }) =>
    [
      queryKeysInvoiceReprocessing.base(),
      'file',
      {
        fileId,
        fileType,
        processId,
      },
    ] as const,
  getForwardingReasons: (processId: UUID) =>
    [
      queryKeysInvoiceReprocessing.base(),
      'forwardingReasons',
      processId,
    ] as const,
  getParsingInput: (processId: UUID) =>
    [queryKeysInvoiceReprocessing.base(), 'parsingInput', processId] as const,
  getProcessCounts: (searchParams: GetReprocessingInvoicesCountsPayload) =>
    [
      queryKeysInvoiceReprocessing.base(),
      'list',
      'aggregatedCounts',
      searchParams,
    ] as const,
  getProcessesFromInvoiceIds: (invoiceId: UUID[]) =>
    [
      queryKeysInvoiceReprocessing.base(),
      'processesFromInvoiceIds',
      { invoiceId },
    ] as const,
  getStatus: (processId: UUID) =>
    [queryKeysInvoiceReprocessing.base(), 'status', processId] as const,
  getStopperRule: (stopperRuleId: UUID) =>
    [
      queryKeysInvoiceReprocessing.base(),
      'stopperRule',
      stopperRuleId,
    ] as const,
  getStopperRules: (searchParams: InvoiceStopperRulesQueryParameters) =>
    [
      queryKeysInvoiceReprocessing.base(),
      'stopperRule',
      'list',
      searchParams,
    ] as const,
  getWatermarks: () =>
    [queryKeysInvoiceReprocessing.base(), 'watermarks'] as const,
} as const;
